import {Injectable} from '@angular/core';
import {ApiService} from '~/app/core/common/services/api.service';
import {Store} from '~/app/core/common/models/store';
import { HttpClient } from '@angular/common/http';
import {BaseService} from '~/app/core/common/services/base.service';
import {orderStatusTxt} from '~/app/core/common/enums/OrderStatusTxt';
import {GoogleAnalyticsService} from '~/app/core/common/services/google-analytics.service';
import {ConfirmationService} from '~/app/core/common/services/confirmation.service';
import {InternetConnectionService} from '~/app/shared/services/internet-connection.service';
import {ErrorApiService} from '~/app/core/common/test/error-api.service';
import {ApplicationSettingsHelper} from '~/app/shared/helpers/application-settings-helper';
import {throwError} from 'rxjs';
import {DialogHelperComponent} from '~/app/shared/helpers/dialog-helper';
import {JwtServiceHelper} from '~/app/shared/helpers/jwt-service-helper';
import {catchError} from "rxjs/operators";
import {ErrorService} from "~/app/core/common/services/error.service";
import {Invoice} from "~/app/core/common/models/invoice";

@Injectable({
    providedIn: 'root'
})
export class StoreListService extends BaseService {

    storeListURL: string;
    invoicesForCustIdURL: string;
    storePicked: Store;
    public _pickerIndex: number;
    public selection: string[] = [];
    public PAGE_SIZE = 20;

    constructor(public http: HttpClient,
                public errorService: ErrorService,
                public confirmationService: ConfirmationService,
                public apiService: ApiService,
                public googleAnalyticsService: GoogleAnalyticsService,
                public internetConnectionService: InternetConnectionService,
                public errorApiService: ErrorApiService,
                public applicationSettingsHelper: ApplicationSettingsHelper,
                public dialogHelper: DialogHelperComponent,
                public jwtService: JwtServiceHelper) {

        super(http,
            googleAnalyticsService,
            internetConnectionService,
            errorApiService,
            applicationSettingsHelper);
    }

    private _userName: string;

    get userName(): string {
        return this._userName;
    }

    set userName(value: string) {
        this._userName = value;
    }

    private _selectedTab: number;

    get selectedTab(): number {
        return this._selectedTab;
    }

    set selectedTab(value: number) {
        this._selectedTab = value;
    }

    private _onFirstLogin = true;

    get onFirstLogin(): boolean {
        return this._onFirstLogin;
    }

    set onFirstLogin(value: boolean) {
        this._onFirstLogin = value;
    }

    private _storeList: Store[];

    get storeList(): Store[] {
        return this._storeList;
    }

    set storeList(value: Store[]) {
        this._storeList = value;
    }

    private _invoiceList: Invoice[];

    get invoiceList(): Invoice[] {
        return this._invoiceList;
    }

    set invoiceList(value: Invoice[]) {
        this._invoiceList = value;
    }

    get pickedStore(): Store {
        return this.storePicked;
    }

    set pickedStore(value: Store) {
        this.storePicked = value;
        if (this.storePicked !== undefined) {
            this.applicationSettingsHelper.setChain(value.chain.toString());
            this.applicationSettingsHelper.setCustomerId(value.customerId.toString());
            this.applicationSettingsHelper.setCustomerNumber(value.customerNumber);
            this.applicationSettingsHelper.setSrcSystem(value.srcSystems);
            var ftpRctString: string = String(value.ftpRct);
            var fileUploadString: string = String(value.fileUpload);
            this.applicationSettingsHelper.setFtpRct(ftpRctString);
            this.applicationSettingsHelper.setFileUpload(fileUploadString);
        }
    }

    get pickedIdentity(): string {
        if (this.storePicked === undefined) {
            return ' ';
        }
        return this.pickedStore.identity;
    }

    get pickedChain(): number {
        if (this.storePicked === undefined) {
            if (this.applicationSettingsHelper.getChain() !== null) {
                return +this.applicationSettingsHelper.getChain();
            } else {
                return -2;
            }
        }
        return this.storePicked.chain;
    }

    get pickedCustomerId(): number {
        if (this.storePicked === undefined) {
            if (this.applicationSettingsHelper.getCustomerId() !== null) {
                return +this.applicationSettingsHelper.getCustomerId();
            } else {
                return -2;
            }
        }
        return this.storePicked.customerId;
    }

    get pickedCustomerNumber(): string {
        if (this.storePicked === undefined) {
            if (this.applicationSettingsHelper.getCustomerId() !== null) {
                return this.applicationSettingsHelper.getCustomerNumber();
            } else {
                return "-2";
            }
        }
        return this.storePicked.customerNumber;
    }

    get pickedSrcSystem(): string {
        if (this.storePicked === undefined) {
            if (this.applicationSettingsHelper.getSrcSystem() !== null) {
                return this.applicationSettingsHelper.getSrcSystem();
            } else {
                return '-2';
            }
        }
        return this.storePicked.srcSystems;
    }

    get isMultiStoreAccount(): boolean {
        if (this.storeList === undefined) {
            return false;
        } else {
            return this.storeList.length > 1;
        }
    }

    async pickedCustomerIdVerify() {
        if (this.pickedCustomerId === -2) {
            await this.load(this.jwtService.getPreferredUserName().toUpperCase());
            return this.pickedCustomerId;
        } else {
            return this.pickedCustomerId;
        }
    }

    async getAllStores(): Promise<Store[]> {
        this.setStoreListURL();
        try {
            this.storeList = await this.getHttp(
                'getAllStores',
                'store-list-service',
                this.storeListURL)
                .toPromise()
                .then(result => result as Store[]);

            this.storeList.forEach(store => {
                store.status = this.GetString(+store.status);
                store.documentType = 'store_list';
            });
        } catch (error) {
            throwError(error);
        }
        return this.storeList;
    }

    async getInvoicesForCustId(chain, custNum, srcSystem) {
        this.setInvoicesForCustIdURL();
        try {
            this.invoiceList = await this.getHttp(
                'getInvoicesForCustId',
                'store-list-service',
                this.invoicesForCustIdURL
                    .replace('chain', chain)
                    .replace('customer_number', encodeURIComponent(custNum))
                    .replace('src_systems', srcSystem)
            ).pipe(
                catchError(this.errorService.handleError('StoreListService getInvoicesForCustId', []))
            )
                .toPromise()
                .then(result => result as Invoice[]);
        } catch (error) {
            throwError(error);
        }
        return this._invoiceList;
    }


    getInvoices(invoice) {
        const data = {
            'invoices': invoice,
            'type' : this.storePicked.srcSystems
          }
        return this.postHttpResponseText(
            'getInvoiceSummary',
            'invoices-service',
            this.apiService.getInvoiceReportURL, JSON.stringify(data))
            .pipe(
                catchError(this.errorService.handleError('StoreListService getInvoices', []))
            )
    }

    public setStoreListURL() {
        this.storeListURL = this.apiService.storeListURL;
    }

    public setInvoicesForCustIdURL() {
        this.invoicesForCustIdURL = this.apiService.invoicesForCustIdURL;
    }

    async updateStoreSelection(customer_number): Promise<Store> {
        if (this.storeList !== undefined) {
            this.storeList.forEach(store => {
                if (store.customerId === customer_number) {
                    this.pickedStore = store;
                }
            });

            this.selection = [];
            this.selection.push(this.pickedStore.customerId.toString());
            this.applicationSettingsHelper.setCustomerId(this.pickedCustomerId.toString());
        }
        // Reset confirmations on new store selection.
        this.confirmationService.showConfirmations = false;
        await this.confirmationService.getStoreConfirmation();
        return this.pickedStore;
    }

    async updateTab() {
        let index;
        if (!this.isMultiStoreAccount) {
            if (this.onFirstLogin) {
                index = 0;
                this.onFirstLogin = false;
            } else {
                index = this.selectedTab;
            }
        }

        if (this.isMultiStoreAccount) {
            if (this.onFirstLogin) {
                index = 1;
                this.onFirstLogin = false;
            } else {
                index = this.selectedTab;
            }
        }
        try {
            this.selectedTab = index;
        
        } catch (e) {
            throwError(e);
        }
    }

    GetString(enumNumber: number) {
        switch (enumNumber) {
            case 0: {
                return orderStatusTxt.NOT_PLACED;
            }
            case 1: {
                return orderStatusTxt.DUE;
            }
            case 2: {
                return orderStatusTxt.STARTED;
            }
            case 3: {
                return orderStatusTxt.PLACED;
            }
            case 4: {
                return orderStatusTxt.CONFIRMED;
            }
            case 5: {
                return orderStatusTxt.CONFIRMED_ACK;
            }
            case 6: {
                return orderStatusTxt.SHIPPED;
            }
            case 7: {
                return orderStatusTxt.DELIVERED;
            }

        }
    }

    async pickFirstStore(channel: string, prevSelected: string, currentUser: string) {
        this.applyStoreChanges(prevSelected, currentUser);
        return this.storePicked;
    }

    allowItemMaint() {
        if (this.pickedStore === undefined) {
            return 'N';
        }
        return this.pickedStore.editItemMaint ? 'Y' : 'N';
    }

    allowCustomizedList() {
        if (this.pickedStore === undefined) {
            return false;
        }
        return this.pickedStore.customListEnabled;
    }

    useItemSequence() {
        if (this.pickedStore === undefined) {
            return 'N';
        }
        return this.pickedStore.usingItemSequence ? 'Y' : 'N';
    }

    load(username: String) {
        return new Promise<void>(async (resolve, reject) => {
            await this.getAllStores()
                .then(async stores => {
                    if (stores !== undefined && stores !== null && stores.length > 0) {
                        await this.pickFirstStore('web', this.applicationSettingsHelper.getStoreSelected(), username.toUpperCase()).then(async store => {
                            await this.updateStoreSelection(store);
                            resolve();
                        });
                    } else {
                        localStorage.clear();
                        reject('An error has occurred making API call for Stores.');
                    }
                }).catch(error => {
                    console.error(error);
                });
        });
    }

    private selectLastSelected(prevSelected: string, currentUser: string) {
        let userName, custId;
        prevSelected.split('-').forEach((item, index) => {
            if (index === 0) {
                userName = item;
            }

            if (index === 1) {
                custId = item;
            }
        });
        if (currentUser === userName) {
            if (this.storeList != undefined) {
                this.storeList.forEach((store, index) => {
                    if (store.customerId.toString() === custId) {
                        this._pickerIndex = index;
                        this.pickedStore = store;
                    }
                });
            }
        }
        if (this.storePicked === undefined) {
            this._pickerIndex = 0;
            if (this.storeList != undefined) {
                this.pickedStore = this.storeList[0];
            }
        }
    }

    private applyStoreChanges(prevSelected: string, currentUser: string) {
        if (prevSelected === null || prevSelected === undefined) {
            this.pickedStore = this.storeList[0];
        } else {
            this.selectLastSelected(prevSelected, currentUser);
        }
    }
}
